<script>
	// Stores
	import UserStore from '@/Stores/UserStore.js';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	// Constants
	import { UserRating, UserRatingName, UserSubRating, UserSubRatingName } from '@/Constants/User.js';
	import TextAreaInput from '@/Components/TextAreaInput.svelte';
	import NoteStore from '@/Stores/User/NoteStore.js';
	import ACLStore from '@/Stores/ACLStore.js';

	export let open = false;
	export let user;

	let sending = false;
	let ratingId = user && user.rating.id;
	let subRatingId = user && user.sub_rating ? user.sub_rating.id : null;
	let error;
	let errorMessage;
	let note = {
		ratingUpdate: ''
	};
	let showNoteBox = false;
	let ratingSwitched = false;

	$: {
		if (ratingId != UserRating.VIP && ratingId != UserRating.VALUED) {
			subRatingId = null;
		} else {
			if (ratingSwitched) {
				subRatingId = UserSubRating.STANDARD;
			} else {
				subRatingId = user?.sub_rating?.id || UserSubRating.STANDARD;
			}
		}
	}
	$: showSubRating = ratingId && (ratingId == UserRating.VIP || ratingId == UserRating.VALUED);
	$: validRatings = getValidRatings();
	$: validSubRatings = getValidSubRatings();

	export function getUserNotes() {
		NoteStore.getUserNotes(user.id, { size: 25, number: 1 });
	}

	function getValidRatings() {
		let ratings = JSON.parse(JSON.stringify(UserRatingName));

		if ($ACLStore && !ACLStore.granted('users.update.vip_rating')) {
			delete ratings[UserRating.VIP];
		}

		delete ratings[UserRating.FEMALE];
		delete ratings[UserRating.TIPPING];
		return Object.entries(ratings);
	}

	function getValidSubRatings() {
		let subRatings = JSON.parse(JSON.stringify(UserSubRatingName));
		return Object.entries(subRatings);
	}

	function confirmClicked() {
		sending = true;
		error = false;
		errorMessage = '';

		if ((ratingId == UserRating.VALUED || ratingId == UserRating.VIP) && !subRatingId) {
			error = true;
			errorMessage = 'Please select a valid user sub-rating.';
			sending = false;
			return;
		}

		UserStore.updateUserRatingAndSubRating(user.id, ratingId, note, subRatingId)
			.then(() => {
				const message = subRatingId ? 'Client Rating and Sub Rating Updated' : 'Client Rating Updated';
				notify({
					type: 'success',
					title: 'Client Rating',
					message: message,
					timeout: 3
				});
				UserStore.getUserWithDetails(user.id);
				getUserNotes();
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Update Client Rating Failed',
					message: (e.error && e.error[0]) || 'Problem updating client rating info',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
				open = false;
			});
	}
</script>

{#if open}
	<Modal on:close={() => (open = false)}>
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">
			{user.first_name}
			{user.last_name} Rating
		</h2>
		<div class="py-2 space-y-1 text-center">
			<div>No. days at {user.rating.name}: {user.rated_days}</div>
			{#if user.projected_ratings?.rnwpad_last_200_bets}
				<div class="font-semibold text-green-500">
					Projected Rating: {UserRatingName[user.projected_ratings?.rnwpad_last_200_bets]}
				</div>
			{/if}
			{#if user.projected_sub_ratings?.rnwpad_last_200_bets}
				<div class="font-semibold text-green-500">
					Projected Sub Rating: {UserSubRatingName[user.projected_sub_ratings?.rnwpad_last_200_bets]}
				</div>
			{/if}
			{#if user.rating.id == UserRating.FEMALE || user.rating.id == UserRating.TIPPING}
				<div class="font-semibold text-red-500">
					Current Rating "{user.rating.name}" is no longer supported.
				</div>
			{/if}
		</div>
		<div class="flex flex-col w-full px-4 text-center">
			<fieldset>
				<div class="my-2 space-y-2">
					{#each validRatings as rating}
						<div class="flex items-center">
							<input
								id={`rating-${rating[0]}`}
								checked={parseInt(rating[0]) === parseInt(user.rating.id)}
								name="user_rating_id"
								type="radio"
								value={rating[0]}
								on:change={() => {
									ratingId = rating[0];
									ratingSwitched = true;
									showNoteBox = true;
									error = false;
								}}
								class="w-4 h-4 focus:outline-none"
							/>
							<label
								for={`rating-${rating[0]}`}
								class="block ml-3 {parseInt(rating[0]) === parseInt(user.projected_rating?.rnw_active_days_200_bets)
									? 'text-green-500 font-semibold'
									: ''}"
							>
								{rating[1]}
							</label>
						</div>
					{/each}
					{#if showNoteBox}
						<div class="flex flex-col w-full px-4 pt-2 text-center">
							<TextAreaInput
								name="note"
								className="pb-4 w-full"
								bind:value={note.ratingUpdate}
								placeholder="Reason for update"
							/>
						</div>
					{/if}
				</div>
			</fieldset>
			<hr class="mt-2 mb-4" />
			{#if showSubRating}
				<fieldset>
					<div class="my-2 space-y-2">
						{#each validSubRatings as subRating}
							<div class="flex items-center">
								<input
									id={`sub-rating-${subRating[0]}`}
									checked={parseInt(subRating[0]) === parseInt(subRatingId)}
									name="user_sub_rating_id"
									type="radio"
									value={subRating[0]}
									on:change={() => {
										subRatingId = subRating[0];
										error = false;
									}}
									class="w-4 h-4 focus:outline-none"
								/>
								<label
									for={`sub-rating-${subRating[0]}`}
									class="block ml-3 {parseInt(subRating[0]) ===
									parseInt(user.projected_sub_rating?.rnw_active_days_200_bets)
										? 'text-green-500 font-semibold'
										: ''}"
								>
									{subRating[1]}
								</label>
							</div>
						{/each}
						{#if error}
							<p class="text-red-500 text-sm mt-1">{errorMessage}</p>
						{/if}
					</div>
				</fieldset>
			{/if}
			<LoadingButton
				on:click={confirmClicked}
				loading={sending}
				disabled={error}
				className="mt-4 mb-4 btn {error ? 'btn-primary-action-disabled' : 'btn-primary'}"
				>Update
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}> Cancel [esc]</button>
		</div>
	</Modal>
{/if}
